import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button, Icon, IconButton } from '@ds/core';
import { useEscNaPilha } from '@ds/feedback';
import { textoDaNotificacao } from '@/data/notificacoes';
import type { Notificacao } from '@/data/notificacoes';
import { dados } from '@/data/porta';

/** "há 5 min", "há 3 h", "ontem" — o bastante para quem abre o sininho. */
export function haQuanto(iso: string, agora = Date.now()): string {
    const minutos = Math.max(0, Math.round((agora - new Date(iso).getTime()) / 60_000));
    if (!Number.isFinite(minutos)) return '';
    if (minutos < 1) return 'agora';
    if (minutos < 60) return `há ${minutos} min`;
    const horas = Math.round(minutos / 60);
    if (horas < 24) return `há ${horas} h`;
    const dias = Math.round(horas / 24);
    return dias === 1 ? 'ontem' : `há ${dias} dias`;
}

/**
 * O painel do sininho (0151).
 *
 * Não é modal nem drawer: é consulta rápida sobre a tela, como um menu — fecha no
 * Esc (a pilha de camadas) e no clique fora, e vai para o `body` por portal pelo
 * mesmo motivo do `Menu` (contêiner com `transform` desloca o `fixed`). A lista só é
 * lida ao abrir. Abrir um aviso leva ao lugar dele e o marca como lido; lido sai.
 */
export function PainelDeNotificacoes({ aberto, aoFechar, aoMudar, aoAbrir }: {
    aberto: boolean;
    aoFechar: () => void;
    /** Algo foi marcado como lido: a contagem do sininho precisa ser relida. */
    aoMudar: () => void;
    aoAbrir: (destino: string) => void;
}) {
    const painelRef = useRef<HTMLDivElement | null>(null);
    const fechar = useRef(aoFechar);
    fechar.current = aoFechar;
    const [lista, setLista] = useState<Notificacao[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);

    useEscNaPilha(aberto, aoFechar);

    useEffect(() => {
        if (!aberto) {
            setLista(null);
            setErro(null);
            return undefined;
        }
        let vivo = true;
        dados().minhasNotificacoes()
            .then((l) => { if (vivo) setLista(l); })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível ler as notificações.'); });

        const aoClicarFora = (e: MouseEvent) => {
            const alvo = e.target as Element | null;
            if (alvo && painelRef.current?.contains(alvo)) return;
            /* O próprio sininho alterna o painel: clicar nele não é "fora". */
            if (alvo?.closest?.('[data-sininho]')) return;
            fechar.current();
        };
        document.addEventListener('mousedown', aoClicarFora);
        return () => {
            vivo = false;
            document.removeEventListener('mousedown', aoClicarFora);
        };
    }, [aberto]);

    const marcar = async (n: Notificacao) => {
        setLista((atual) => atual?.filter((x) => x.id !== n.id) ?? null);
        try {
            await dados().marcarNotificacaoLida(n.id);
        } catch {
            /* O aviso volta na próxima leitura: não há o que desfazer na tela. */
        }
        aoMudar();
    };

    const abrir = async (n: Notificacao) => {
        const { destino } = textoDaNotificacao(n);
        await marcar(n);
        fechar.current();
        aoAbrir(destino);
    };

    const marcarTodas = async () => {
        const antes = lista;
        setLista([]);
        try {
            await dados().marcarTodasNotificacoesLidas();
        } catch (e) {
            setLista(antes);
            setErro(e instanceof Error ? e.message : 'Não foi possível marcar como lidas.');
        }
        aoMudar();
    };

    if (!aberto || typeof document === 'undefined') return null;

    return createPortal(
        <div
            ref={painelRef}
            role="dialog"
            aria-label="Notificações"
            style={{
                position: 'fixed', top: 'calc(60px + env(safe-area-inset-top, 0px))', right: 16, zIndex: 80,
                width: 'min(380px, calc(100vw - 32px))', maxHeight: 'min(70vh, 560px)',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-lg)', fontFamily: 'var(--font-ui)',
            }}
        >
            <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 10px 10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
                <h2 style={{ flex: 1, margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Notificações</h2>
                {!!lista?.length && (
                    <Button size="sm" variant="ghost" onClick={() => { void marcarTodas(); }}>Marcar todas como lidas</Button>
                )}
            </header>

            <div style={{ overflowY: 'auto' }}>
                {erro && (
                    <p role="alert" style={{ margin: 0, padding: 14, fontSize: 13, color: 'var(--danger-strong)' }}>{erro}</p>
                )}
                {!erro && lista === null && (
                    <p role="status" style={{ margin: 0, padding: 14, fontSize: 13, color: 'var(--text-secondary)' }}>Carregando…</p>
                )}
                {!erro && lista?.length === 0 && (
                    <p style={{ margin: 0, padding: '24px 14px', fontSize: 13, textAlign: 'center', color: 'var(--text-secondary)' }}>
                        Nenhum aviso novo. O que você lê sai daqui.
                    </p>
                )}
                {!!lista?.length && (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {lista.map((n, i) => {
                            const t = textoDaNotificacao(n);
                            return (
                                <li key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 8px 11px 14px', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)' }}>
                                    <Icon name={t.icone} size={16} style={{ color: 'var(--neutral-600)', marginTop: 2, flex: '0 0 auto' }} />
                                    <button
                                        type="button"
                                        onClick={() => { void abrir(n); }}
                                        style={{ flex: 1, minWidth: 0, border: 0, padding: 0, background: 'transparent', textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
                                    >
                                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{t.titulo}</span>
                                        {t.texto && <span style={{ display: 'block', fontSize: 12.5, lineHeight: '17px', color: 'var(--text-secondary)' }}>{t.texto}</span>}
                                        <span style={{ display: 'block', marginTop: 2, fontSize: 12, color: 'var(--text-tertiary)' }}>{t.grupo} · {haQuanto(n.criadaEm)}</span>
                                    </button>
                                    <IconButton icon={<Icon name="check" size={15} />} label={`Marcar "${t.titulo}" como lida`} onClick={() => { void marcar(n); }} />
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>,
        document.body,
    );
}
